import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, Timer } from 'lucide-react';
import { NumericInput } from '../../components/NumericInput';
import type {
  TrainingExerciseEntry,
  TrainingSessionExerciseLog,
  TrainingSessionLoggedSet,
} from '../../types';

const DEFAULT_REST_SEC = 90;

function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export interface ActiveWorkoutSessionProps {
  planName: string;
  dayTitle: string;
  exercises: TrainingExerciseEntry[];
  onFinish: (
    log: TrainingSessionExerciseLog[],
    startedAtIso: string,
    endedAtIso: string
  ) => Promise<void>;
  onExit: () => void;
}

const ActiveWorkoutSession: React.FC<ActiveWorkoutSessionProps> = ({
  planName,
  dayTitle,
  exercises,
  onFinish,
  onExit,
}) => {
  const startedAtRef = useRef<string>(new Date().toISOString());
  const [elapsedSec, setElapsedSec] = useState(0);
  const [restUntil, setRestUntil] = useState<number | null>(null);
  const [restSecLeft, setRestSecLeft] = useState(0);
  const [restDuration, setRestDuration] = useState(DEFAULT_REST_SEC);
  const [finishing, setFinishing] = useState(false);

  /** entryId -> logged sets in order (one per planned set when completed) */
  const [strengthSets, setStrengthSets] = useState<Record<string, TrainingSessionLoggedSet[]>>({});
  /** entryId -> setIndex -> draft actual reps/kg for that row */
  const [rowDrafts, setRowDrafts] = useState<
    Record<string, Record<number, { reps: number; weightKg: number }>>
  >({});
  /** Mirrors rowDrafts so "Log set" reads latest values after NumericInput blur/commit */
  const rowDraftsRef = useRef(rowDrafts);
  const [cardioDone, setCardioDone] = useState<Record<string, { minutes: number; done: boolean }>>(
    {}
  );
  const cardioDoneRef = useRef(cardioDone);

  useEffect(() => {
    const m: Record<string, Record<number, { reps: number; weightKg: number }>> = {};
    exercises.forEach((ex) => {
      if (ex.kind !== 'strength') return;
      m[ex.entryId] = {};
      (ex.setTargets ?? []).forEach((row, i) => {
        m[ex.entryId][i] = { reps: row.reps, weightKg: row.weightKg };
      });
    });
    setRowDrafts(m);
    setStrengthSets({});
  }, [exercises]);

  useEffect(() => {
    rowDraftsRef.current = rowDrafts;
  }, [rowDrafts]);

  useEffect(() => {
    cardioDoneRef.current = cardioDone;
  }, [cardioDone]);

  useEffect(() => {
    const t = window.setInterval(() => {
      setElapsedSec(
        Math.floor((Date.now() - new Date(startedAtRef.current).getTime()) / 1000)
      );
    }, 1000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (restUntil == null) return;
    const tick = () => {
      const left = Math.ceil((restUntil - Date.now()) / 1000);
      setRestSecLeft(Math.max(0, left));
      if (left <= 0) setRestUntil(null);
    };
    tick();
    const t = window.setInterval(tick, 500);
    return () => window.clearInterval(t);
  }, [restUntil]);

  const startRest = () => {
    setRestUntil(Date.now() + restDuration * 1000);
    setRestSecLeft(restDuration);
  };

  const logStrengthSet = (ex: TrainingExerciseEntry, setIndex: number) => {
    if (ex.kind !== 'strength') return;
    const logged = strengthSets[ex.entryId] ?? [];
    if (setIndex !== logged.length) return;
    const d = rowDraftsRef.current[ex.entryId]?.[setIndex];
    if (!d) return;
    setStrengthSets((prev) => ({
      ...prev,
      [ex.entryId]: [...(prev[ex.entryId] ?? []), { reps: d.reps, weightKg: d.weightKg }],
    }));
    startRest();
  };

  const completeCardio = (ex: TrainingExerciseEntry) => {
    if (ex.kind !== 'cardio') return;
    const row = cardioDoneRef.current[ex.entryId];
    const minutes = row?.minutes ?? ex.targetMinutes ?? 15;
    setCardioDone((prev) => ({
      ...prev,
      [ex.entryId]: { minutes, done: true },
    }));
  };

  const handleFinish = async () => {
    const log: TrainingSessionExerciseLog[] = exercises.map((ex) => {
      if (ex.kind === 'cardio') {
        const c = cardioDone[ex.entryId];
        return {
          entryId: ex.entryId,
          kind: 'cardio' as const,
          minutesDone: c?.done ? c.minutes : 0,
        };
      }
      return {
        entryId: ex.entryId,
        kind: 'strength' as const,
        sets: strengthSets[ex.entryId] ?? [],
      };
    });
    try {
      setFinishing(true);
      const ended = new Date().toISOString();
      await onFinish(log, startedAtRef.current, ended);
    } finally {
      setFinishing(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 pb-44">
      <header className="sticky top-0 z-30 bg-zinc-950/95 border-b border-zinc-800 px-3 py-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onExit}
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-300 shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-zinc-500 truncate">{planName}</p>
          <p className="text-sm font-semibold text-white truncate">{dayTitle}</p>
        </div>
        <button
          type="button"
          onClick={handleFinish}
          disabled={finishing}
          className="shrink-0 px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-500 disabled:opacity-50"
        >
          {finishing ? 'Saving…' : 'Save workout'}
        </button>
        <div className="flex items-center gap-1 text-green-400 font-mono text-base tabular-nums shrink-0">
          <Timer className="w-4 h-4" />
          {formatElapsed(elapsedSec)}
        </div>
      </header>

      {restUntil != null && restSecLeft > 0 && (
        <div className="mx-4 mt-4 p-4 rounded-2xl bg-zinc-900 border border-green-600/40 text-center">
          <p className="text-xs text-zinc-400 uppercase tracking-wide mb-1">Rest</p>
          <p className="text-4xl font-bold text-green-400 tabular-nums">{restSecLeft}</p>
          <p className="text-xs text-zinc-500 mt-2">seconds</p>
          <button
            type="button"
            onClick={() => setRestUntil(null)}
            className="mt-3 text-sm text-zinc-400 underline"
          >
            Skip rest
          </button>
        </div>
      )}

      <div className="px-4 py-4 space-y-6 max-w-lg mx-auto">
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span>Rest between sets</span>
          <select
            value={restDuration}
            onChange={(e) => setRestDuration(Number(e.target.value))}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200"
          >
            {[60, 90, 120, 180].map((s) => (
              <option key={s} value={s}>
                {s}s
              </option>
            ))}
          </select>
        </div>

        {exercises.map((ex) => (
          <section
            key={ex.entryId}
            className="rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-zinc-800">
              <h3 className="text-lg font-bold text-white leading-tight">
                {ex.name.trim() || 'Exercise'}
              </h3>
              {ex.kind === 'strength' ? (
                <p className="text-xs text-zinc-500 mt-1">
                  {(ex.setTargets ?? []).length} planned set
                  {(ex.setTargets ?? []).length !== 1 ? 's' : ''} — log in order (Set 1, then 2, …)
                </p>
              ) : (
                <p className="text-xs text-zinc-500 mt-1">
                  Cardio · ~{ex.targetMinutes ?? 15} min planned
                </p>
              )}
            </div>

            {ex.kind === 'strength' && (
              <div className="p-4 space-y-3">
                {(ex.setTargets ?? []).length === 0 ? (
                  <p className="text-sm text-zinc-500">No sets in this exercise.</p>
                ) : (
                  (ex.setTargets ?? []).map((target, i) => {
                    const loggedArr = strengthSets[ex.entryId] ?? [];
                    const isDone = i < loggedArr.length;
                    const isNext = i === loggedArr.length;
                    const draft = rowDrafts[ex.entryId]?.[i] ?? {
                      reps: target.reps,
                      weightKg: target.weightKg,
                    };

                    return (
                      <div
                        key={target.setId}
                        className={`rounded-xl border p-3 ${
                          isDone
                            ? 'border-green-700/50 bg-green-950/20'
                            : isNext
                              ? 'border-zinc-600 bg-zinc-800/50'
                              : 'border-zinc-800 opacity-60'
                        }`}
                      >
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <span className="text-sm font-medium text-zinc-200">Set {i + 1}</span>
                          <span className="text-xs text-zinc-500">
                            Plan: {target.reps} reps
                            {target.weightKg <= 0 ? ' · BW' : ` · ${target.weightKg} kg`}
                          </span>
                        </div>
                        {isDone && (
                          <p className="text-xs text-green-400 mb-2">
                            Logged: {loggedArr[i].reps} reps @{' '}
                            {loggedArr[i].weightKg <= 0 ? 'BW' : `${loggedArr[i].weightKg} kg`}
                          </p>
                        )}
                        {isNext && (
                          <>
                            <div className="flex gap-2 mb-2">
                              <div className="flex-1">
                                <label className="text-xs text-zinc-500">Reps</label>
                                <NumericInput
                                  value={draft.reps}
                                  min={1}
                                  className="w-full mt-1 px-3 py-2 rounded-xl bg-zinc-800 border border-zinc-700 text-white"
                                  onCommit={(reps) =>
                                    setRowDrafts((d) => ({
                                      ...d,
                                      [ex.entryId]: {
                                        ...d[ex.entryId],
                                        [i]: { ...draft, reps },
                                      },
                                    }))
                                  }
                                />
                              </div>
                              <div className="flex-1">
                                <label className="text-xs text-zinc-500">kg</label>
                                <NumericInput
                                  value={draft.weightKg}
                                  min={0}
                                  decimals
                                  className="w-full mt-1 px-3 py-2 rounded-xl bg-zinc-800 border border-zinc-700 text-white"
                                  onCommit={(weightKg) =>
                                    setRowDrafts((d) => ({
                                      ...d,
                                      [ex.entryId]: {
                                        ...d[ex.entryId],
                                        [i]: { ...draft, weightKg },
                                      },
                                    }))
                                  }
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                (document.activeElement as HTMLElement | undefined)?.blur();
                                window.setTimeout(() => logStrengthSet(ex, i), 0);
                              }}
                              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 text-white font-semibold text-sm"
                            >
                              <Check className="w-4 h-4" />
                              Log set {i + 1}
                            </button>
                          </>
                        )}
                        {!isDone && !isNext && (
                          <p className="text-xs text-zinc-600">Complete earlier sets first.</p>
                        )}
                      </div>
                    );
                  })
                )}
                <div className="text-xs text-zinc-500">
                  Logged {(strengthSets[ex.entryId]?.length ?? 0)} / {(ex.setTargets ?? []).length}{' '}
                  sets
                </div>
              </div>
            )}

            {ex.kind === 'cardio' && (
              <div className="p-4 space-y-3">
                <div>
                  <label className="text-xs text-zinc-500">Minutes completed</label>
                  <NumericInput
                    value={cardioDone[ex.entryId]?.minutes ?? ex.targetMinutes ?? 15}
                    min={1}
                    className="w-full mt-1 px-3 py-2 rounded-xl bg-zinc-800 border border-zinc-700 text-white"
                    onCommit={(minutes) =>
                      setCardioDone((p) => ({
                        ...p,
                        [ex.entryId]: {
                          minutes,
                          done: p[ex.entryId]?.done ?? false,
                        },
                      }))
                    }
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    (document.activeElement as HTMLElement | undefined)?.blur();
                    window.setTimeout(() => completeCardio(ex), 0);
                  }}
                  disabled={cardioDone[ex.entryId]?.done}
                  className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-500 disabled:opacity-40 text-white font-semibold text-sm"
                >
                  {cardioDone[ex.entryId]?.done ? 'Done' : 'Mark cardio done'}
                </button>
              </div>
            )}
          </section>
        ))}
      </div>

      {/* bottom-16 clears app BottomBar (h-16, z-40); z-50 keeps save above it */}
      <div className="fixed bottom-16 left-0 right-0 z-50 p-4 pt-2 bg-zinc-950 border-t border-zinc-800 shadow-[0_-8px_24px_rgba(0,0,0,0.5)] max-w-lg mx-auto">
        <button
          type="button"
          onClick={handleFinish}
          disabled={finishing}
          className="w-full py-3.5 rounded-2xl bg-white text-zinc-900 font-bold text-sm hover:bg-zinc-200 disabled:opacity-50"
        >
          {finishing ? 'Saving…' : 'Save workout'}
        </button>
      </div>
    </div>
  );
};

export default ActiveWorkoutSession;
